import React, { useEffect, useState } from "react";
import KeyboardBackspaceIcon from "@mui/icons-material/KeyboardBackspace";
import { Link, useNavigate, useParams } from "react-router-dom";
import Sidebar from "../components/Sidebar";
import { Stack } from "@mui/material";
import Autocomplete, { createFilterOptions } from "@mui/material/Autocomplete";
import TextField from "@mui/material/TextField";
import CircularProgress from "@mui/material/CircularProgress";
import { toast } from "react-toastify";
import { useMutation, useQuery } from "react-query";
import API from "../APIs/API";
import "../styles/profile.scss";

const limitedFilter = createFilterOptions({ limit: 50 });

const initialData = {
  employee_no: null,
  admin_location: "",
  admin_desc: "",
  position_no: "",
  occup_pos_title: "",
  award: "",
  award_desc: "",
  classification: "",
  class_desc: "",
  occup_type: "",
  account_no: "",
  account_no_desc: "",
  paypoint: "",
  paypoint_desc: "",
  occup_pos_cat: "",
};

// Plain read-only / disabled text input (for non-dropdown fields or view mode)
const PlainField = ({ label, name, value, editable, readOnly = false, onChange }) => (
  <div className="form-outline" style={{ marginBottom: 12 }}>
    <label className="form-label" htmlFor={name}>
      <b>{label}</b>
    </label>
    <br />
    <input
      className="form-control"
      type="text"
      id={name}
      name={name}
      value={value ?? ""}
      disabled={readOnly || !editable}
      onChange={onChange}
    />
  </div>
);

// Autocomplete with freeSolo — shows dropdown options from DB but also allows manual entry
const DropdownField = ({ label, name, value, editable, readOnly = false, onChange, options = [] }) => {
  if (!editable || readOnly) {
    return (
      <div className="form-outline" style={{ marginBottom: 12 }}>
        <label className="form-label" htmlFor={name}>
          <b>{label}</b>
        </label>
        <br />
        <input
          className="form-control"
          type="text"
          id={name}
          name={name}
          value={value ?? ""}
          disabled
        />
      </div>
    );
  }

  return (
    <div className="form-outline" style={{ marginBottom: 12 }}>
      <label className="form-label" htmlFor={name}>
        <b>{label}</b>
      </label>
      <Autocomplete
        freeSolo
        filterOptions={limitedFilter}
        options={options}
        value={value ?? ""}
        // When user picks from dropdown list
        onChange={(_, newValue) => {
          onChange({ target: { name, value: newValue ?? "" } });
        }}
        // When user types manually
        onInputChange={(_, newValue, reason) => {
          if (reason === "input") {
            onChange({ target: { name, value: newValue ?? "" } });
          }
        }}
        renderInput={(params) => (
          <TextField
            {...params}
            size="small"
            variant="outlined"
            name={name}
            sx={{
              "& .MuiOutlinedInput-root": {
                backgroundColor: "#fff",
                fontSize: 14,
              },
            }}
          />
        )}
        sx={{ mt: 0.5 }}
      />
    </div>
  );
};

const StaffView = ({ type }) => {
  const navigate = useNavigate();
  // The route param is the DB id (works for both employees and position-only records)
  const { admin_location_id, employee_no: staff_id } = useParams();
  const pageUserType = type === "Non-Teacher" ? "non-teachers" : "teachers";
  const backUrl = `/${pageUserType}/${admin_location_id}/staff`;

  const [staff, setStaff] = useState(initialData);
  const [editable, setEditable] = useState(false);
  const [needSave, setNeedSave] = useState(false);

  // Load record by DB id (works for both real employees and position-only records)
  useEffect(() => {
    new API().getStaffEstablishment(staff_id).then((data) => {
      if (data?.data) {
        setStaff(data.data);
      } else {
        toast.error("Record not found");
        navigate(backUrl);
      }
    });
  }, [staff_id]);

  // Load field options only when edit mode is active (lazy → faster page load).
  // Shared cache key means subsequent pages reuse the cached result instantly.
  const { data: fieldOptions = {} } = useQuery(["staff_field_options"], {
    queryFn: async () => {
      const result = await new API().getStaffFieldOptions();
      return result?.data ?? {};
    },
    staleTime: 5 * 60 * 1000,
    enabled: editable,
  });

  const handleChange = (e) => {
    if (!needSave) setNeedSave(true);
    setStaff((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const updateMutation = useMutation(
    ({ id, ...fields }) => new API().updateStaffEstablishment(id, fields),
    {
      onSuccess: (data) => {
        if (data?.status === "ok") {
          toast.success("Record updated successfully!");
          setEditable(false);
          setNeedSave(false);
        } else {
          toast.error("Update failed!");
        }
      },
      onError: () => toast.error("Update failed!"),
    }
  );

  const deleteMutation = useMutation(
    (id) => new API().deleteStaffEstablishment(id),
    {
      onSuccess: (data) => {
        if (data?.status === "ok") {
          toast.success("Record deleted successfully!");
          navigate(backUrl);
        } else {
          toast.error("Delete failed!");
        }
      },
      onError: () => toast.error("Delete failed!"),
    }
  );

  const handleDelete = () => {
    if (window.confirm("Are you sure you want to delete this record? This action cannot be undone.")) {
      deleteMutation.mutate(staff.id);
    }
  };

  const handleSave = (e) => {
    e.preventDefault();
    updateMutation.mutate({
      id: staff.id,
      position_no:    staff.position_no,
      occup_pos_title: staff.occup_pos_title,
      award:          staff.award,
      award_desc:     staff.award_desc,
      classification: staff.classification,
      class_desc:     staff.class_desc,
      occup_type:     staff.occup_type,
      account_no:     staff.account_no,
      account_no_desc: staff.account_no_desc,
      paypoint:       staff.paypoint,
      paypoint_desc:  staff.paypoint_desc,
      occup_pos_cat:  staff.occup_pos_cat,
    });
  };

  // Field definitions — readOnly: always disabled; dropdown: use Autocomplete with DB options
  const fields = [
    { label: "Admin Location",  name: "admin_location",  readOnly: true,  dropdown: false },
    { label: "Admin Desc",      name: "admin_desc",      readOnly: true,  dropdown: false },
    { label: "Position No",     name: "position_no",     readOnly: false, dropdown: true  },
    { label: "Occup Pos Title", name: "occup_pos_title", readOnly: false, dropdown: true  },
    { label: "Award",           name: "award",           readOnly: false, dropdown: true  },
    { label: "Award Desc",      name: "award_desc",      readOnly: false, dropdown: true  },
    { label: "Classification",  name: "classification",  readOnly: false, dropdown: true  },
    { label: "Class Desc",      name: "class_desc",      readOnly: false, dropdown: true  },
    { label: "Occup Type",      name: "occup_type",      readOnly: false, dropdown: true  },
    { label: "Account No",      name: "account_no",      readOnly: false, dropdown: true  },
    { label: "Account No Desc", name: "account_no_desc", readOnly: false, dropdown: true  },
    { label: "Paypoint",        name: "paypoint",        readOnly: false, dropdown: true  },
    { label: "Paypoint Desc",   name: "paypoint_desc",   readOnly: false, dropdown: true  },
    { label: "Occup Pos Cat",   name: "occup_pos_cat",   readOnly: false, dropdown: true  },
  ];

  const renderField = (f) => {
    const props = {
      key: f.name,
      label: f.label,
      name: f.name,
      value: staff[f.name],
      editable,
      readOnly: f.readOnly,
      onChange: handleChange,
    };
    if (f.dropdown) {
      return <DropdownField {...props} options={fieldOptions[f.name] ?? []} />;
    }
    return <PlainField {...props} />;
  };

  return (
    <div className="wrapper">
      <div className="sidebar">
        <Sidebar />
      </div>
      <div className="content">
        <div className="profile">
          <div className="profileContainer">
            <div className="profileSection">
              <Link to={backUrl} className="backButtonSection">
                <KeyboardBackspaceIcon className="backButton" />
              </Link>
              <br />
              <br />

              <form>
                <div className="container">
                  <div className="row">
                    <div className="col col-sm">
                      <h1>Staff Record Details</h1>
                    </div>
                  </div>
                  <hr />

                  {/* Establishment Status — derived from whether an employee is assigned */}
                  <div className="row" style={{ marginBottom: 8 }}>
                    <div className="col col-sm">
                      <div className="form-outline" style={{ marginBottom: 12 }}>
                        <label className="form-label">
                          <b>Establishment Status</b>
                        </label>
                        <div
                          style={{
                            display: "flex",
                            gap: 32,
                            marginTop: 8,
                            pointerEvents: "none",
                            userSelect: "none",
                          }}
                        >
                          <label style={{ display: "flex", alignItems: "center", gap: 8, cursor: "default" }}>
                            <input
                              type="radio"
                              name="establishment_status"
                              value="Occupied"
                              checked={staff.employee_no !== null && staff.employee_no !== undefined}
                              onChange={() => {}}
                              style={{ width: 16, height: 16 }}
                            />
                            <span style={{ fontSize: 15 }}>Occupied</span>
                          </label>
                          <label style={{ display: "flex", alignItems: "center", gap: 8, cursor: "default" }}>
                            <input
                              type="radio"
                              name="establishment_status"
                              value="Vacant"
                              checked={staff.employee_no === null || staff.employee_no === undefined}
                              onChange={() => {}}
                              style={{ width: 16, height: 16 }}
                            />
                            <span style={{ fontSize: 15 }}>Vacant</span>
                          </label>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="row">
                    <div className="col col-sm">
                      {fields.slice(0, 7).map(renderField)}
                    </div>
                    <div className="col col-sm">
                      {fields.slice(7).map(renderField)}
                    </div>
                  </div>

                  <hr />
                  <Stack direction="row" spacing={1.5} sx={{ gap: '12px' }}>
                    <Link
                      className="app-button app-button-gray"
                      to={backUrl}
                    >
                      Back
                    </Link>
                    <button
                      type="button"
                      className="app-button app-button-primary"
                      disabled={editable}
                      onClick={() => setEditable(true)}
                    >
                      Edit
                    </button>
                    <button
                      type="button"
                      className="app-button app-button-primary"
                      disabled={!needSave || updateMutation.isLoading}
                      onClick={handleSave}
                    >
                      {updateMutation.isLoading ? (
                        <>
                          <CircularProgress
                            size={14}
                            color="inherit"
                            style={{ marginRight: 6 }}
                          />
                          Saving…
                        </>
                      ) : (
                        "Save"
                      )}
                    </button>
                    <button
                      type="button"
                      className="app-button app-button-primary"
                      style={{ backgroundColor: "#c0392b" }}
                      disabled={deleteMutation.isLoading}
                      onClick={handleDelete}
                    >
                      {deleteMutation.isLoading ? "Deleting…" : "Delete"}
                    </button>
                  </Stack>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default StaffView;
